import { useState, useCallback } from 'react';

import { useData } from '@/components/data-provider';
import { offlineSyncService } from '@/services/offline-sync-service';

export interface SubmitState {
	isSubmitting: boolean;
	error: Error | null;
}

export interface SubmitOptions {
	offlineMessage?: string;
	/**
	 * When set, the mutation will be queued for offline sync instead of
	 * blocking.  Provide the API endpoint, method, body, and a label.
	 */
	offlineQueue?: {
		endpoint: string;
		method: string;
		body: Record<string, unknown>;
		label: string;
	};
}

export function useSubmit() {
	const [isSubmitting, setSubmitting] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [queued, setQueued] = useState(false);
	const { isOnline } = useData();

	const run = useCallback(async <T,>(fn: () => Promise<T>, options?: SubmitOptions): Promise<T> => {
		if (!isOnline) {
			// If caller provides queue info, queue the mutation instead of blocking
			if (options?.offlineQueue) {
				await offlineSyncService.queueMutation(options.offlineQueue);
				setQueued(true);
				// Return undefined — the caller should check `queued` state
				return undefined as T;
			}
			const offlineError = new Error(options?.offlineMessage ?? 'You appear to be offline. Please reconnect and try again.');
			setError(offlineError);
			throw offlineError;
		}

		setSubmitting(true);
		setError(null);
		setQueued(false);
		try {
			return await fn();
		} catch (e: any) {
			const err = e instanceof Error ? e : new Error(e?.message || 'Request failed');
			setError(err);
			throw err;
		} finally {
			setSubmitting(false);
		}
	}, [isOnline]);

	return { isSubmitting, error, setError, queued, run } as const;
}
